<template>
    <div class="sticky top-0 w-[100%] pt-2 pb-4 bg-white flex justify-between items-center" style="z-index:100">
        <div class="flex flex-col">
            <h4 class="font-heading-sm-semibold">Previous Employer(s)</h4>
            <p
                v-if="isEditingMode"
                class="font-body-sm-regular text-accent-red-3"
            >
                You are in the editing mode now. Please click the save button.
            </p>
        </div>
        <div v-if="$appUtil.checkPermission('PREVIOUS_EMPLOYER_INFO_EDIT')">
            <div v-if="!isEditingMode">
                <DesignButton
                    class="w-[59px]"
                    btnText="Edit"
                    btnSize="SM"
                    btnType="PRIMARY"
                    btnVariant="BORDER"
                    :btnDisabled="false"
                    @click-event="startEditPrevious"
                >
                    <template #front-icon>
                        <EditIcon :width="16" :height="16" color="text-primary-3" weight="REGULAR" />
                    </template>
                </DesignButton>
            </div>
            <div v-else class="flex items-center gap-1">
                <DesignButton
                    class="w-[64px]"
                    btnText="Cancel"
                    btnSize="SM"
                    btnType="SECONDARY"
                    btnVariant="BORDER"
                    :btnDisabled="false"
                    @click-event="cancelEditPrevious"
                />
                <DesignButton
                    class="w-[64px]"
                    btnText="Save"
                    btnSize="SM"
                    btnType="PRIMARY"
                    btnVariant="FILL"
                    :btnDisabled="false"
                    @click-event="insertOrEditPreviousSalary"
                />
            </div>
        </div>
    </div>

    <div class="flex flex-col gap-16">
        <!--Previous Employer(s) START-->
        <div>
            <h4 class="font-body-md-semibold">Previous Employer(s)</h4>
            <div class="flex justify-between items-center gap-2 mt-6">
                <div class="c_label w-[8rem]">Employer Name</div>
                <DesignInput 
                    v-model="employeePreviousSalaryData.employerName"
                    :disabled="!isEditingMode"
                    :placeholder="isEditingMode ? 'Enter the Employer Name' : '-'"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">Employer TIN</div>
                <DesignInput
                    v-model="formattedTin"
                    :disabled="!isEditingMode"
                    :placeholder="isEditingMode ? 'Enter the Employer TIN(000-000-000-00000)' : '-'"
                    class="flex-1"
                    @input="changeTinNumberFormat($event.target.value)"
                    :maxlength="17"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">Employment Type</div>
                <DesignDropdownSelect 
                    v-model="employeePreviousSalaryData.employmentType"
                    :disabled="!isEditingMode"
                    :options="employmentTypeOption" 
                    placeholder="Select Employment Type" 
                    class="flex-1"
                    width="w-full"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">Address</div>
                <DesignInput
                    v-model="employeePreviousSalaryData.address"
                    :disabled="!isEditingMode"
                    :placeholder="isEditingMode ? 'Enter the Address' : '-'"
                    class="flex-1" 
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">ZIP Code</div>
                <DesignInput
                    v-model="employeePreviousSalaryData.zipCode"
                    :disabled="!isEditingMode"
                    :placeholder="isEditingMode ? 'Enter the ZIP Code' : '-'"
                    class="flex-1" 
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">Taxable Year</div>
                <DesignDropdownSelect 
                    v-model="employeePreviousSalaryData.taxableYear"
                    :disabled="!isEditingMode"
                    :options="yearOptions" 
                    placeholder="Select the Taxable Year" 
                    class="flex-1" 
                    width="w-full"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">From Date</div>
                <DesignDatepicker
                    :modelValue="toDatePickerValue(employeePreviousSalaryData.taxableFrom)"
                    @update:output="employeePreviousSalaryData.taxableFrom = $event"
                    :disabled="!isEditingMode"
                    :placeholder="isEditingMode ? 'MM/DD' : '-'"
                    :format="'MMDD'"
                    class="flex-1"
                    displayFormat="MM/dd"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">To Date</div>
                <DesignDatepicker
                    :modelValue="toDatePickerValue(employeePreviousSalaryData.taxableTo)"
                    @update:output="employeePreviousSalaryData.taxableTo = $event"
                    :disabled="!isEditingMode"
                    :placeholder="isEditingMode ? 'MM/DD' : '-'"
                    :format="'MMDD'"
                    class="flex-1"
                    displayFormat="MM/dd"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[8rem]">Resignation Type</div>
                <DesignDropdownSelect 
                    v-model="employeePreviousSalaryData.resignationType"
                    :disabled="!isEditingMode"
                    :options="resignationTypeOption" 
                    placeholder="Select Resignation Type" 
                    class="flex-1" 
                    width="w-full"
                />
            </div>
        </div>
        <!--Previous Employer(s) END-->

        <!--NON-TAXABLE(From Previous Employer) START-->
        <div>
            <h4 class="font-body-md-semibold">NON-TAXABLE(From Previous Employer)</h4>
            <div class="flex justify-between items-center gap-2 mt-6">
                <div class="c_label w-[12rem]">Basic Salary<br />(P250,000 And Below)</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.nonTaxableBasicSalary"
                    :placeholder="isEditingMode ? 'Enter the non taxable basic salary' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">13th Month & Other Benefits</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.nonTaxable13thMonthAndOtherBenefits"
                    :placeholder="isEditingMode ? 'Enter the non taxable 13th Month & Other Benefits' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">De Minimis Benefits</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.nonTaxableDeminimisBenefits"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">SSS, GSIS, PAG-IBIG<br />& Union Dues</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.nonTaxableSssGsisPagibigAndUnionDues"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">Salaries & Other<br />Forms Of Compensation</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.nonTaxableSalariesAndOtherFormsOfCompensation"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">Total Non-Taxable/Exempt<br />Compensation Income</div>
                <DesignAmountInput
                    :modelValue="totalNonTaxableCompensation"
                    class="flex-1"
                    :disabled="true"
                    :placeholder="'0'"
                />
            </div>
        </div>
        <!--NON-TAXABLE(From Previous Employer) END-->

        <!--TAXABLE(From Previous Employer) START-->
        <div>
            <h4 class="font-body-md-semibold">TAXABLE(From Previous Employer)</h4>
            <div class="flex justify-between items-center gap-2 mt-6">
                <div class="c_label w-[12rem]">Basic Salary</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.taxableBasicSalary"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">13th Month & Other Benefits</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.taxable13thMonthAndOtherBenefits"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">Salaries & Other Compensation</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.taxableSalariesOtherCompensation"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">Total Taxable Compensation Income</div>
                <DesignAmountInput
                    :modelValue="totalTaxableCompensation"
                    class="flex-1"
                    :disabled="true"
                    :placeholder="'0'"
                />
            </div>
        </div>
        <!--TAXABLE(From Previous Employer) END-->

        <!--TAX WITHHELD(From Previous Employer) START-->
        <div>
            <h4 class="font-body-md-semibold">TAX WITHHELD(From Previous Employer)</h4>
            <div class="flex justify-between items-center gap-2 mt-4">
                <div class="c_label w-[12rem]">Salaries & Other Compensation</div>
                <DesignAmountInput
                    v-model="employeePreviousSalaryData.taxWithheldPresentEmployer"
                    :placeholder="isEditingMode ? 'Enter the amount' : '0'"
                    :disabled="!isEditingMode"
                    class="flex-1"
                />
            </div>
        </div>
        <!--TAX WITHHELD(From Previous Employer) END-->
    </div>
</template>

<script>
import DesignInput from '@/components/design-system/design-input.vue';
import DesignAmountInput from '@/components/design-system/design-amount-input.vue';
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import DesignDatepicker from '@/components/design-system/design-datepicker.vue';
import EditIcon from '@/components/design-system/icon/edit.vue';
import { ValidateUtil } from '@/plugins/app-util';
import moment from 'moment';

export default {
    components: {
        DesignInput,
        DesignAmountInput,
        DesignDropdownSelect,
        DesignButton,
        DesignDatepicker,
        EditIcon,
    },
    props: {
        employeeData: Object,
    },
    data() {
        return {
            isEditingMode: false,
            showEditBtn: false,
            yearOptions: [],
            resignationTypeOption : [
                { value : 'T',  text : "Terminated/Resigned" }, 
                { value : 'TR', text : "Transferred" },
                { value : 'R',  text : "Retirement" }, 
                { value : 'D',  text : "Death" }, 
            ],
            employmentTypeOption : [
                { value : 'R',  text : 'Regular'}, 
                { value : 'C',  text : 'Casual'}, 
                { value : 'CP', text : 'Contractual/Project-Based'}, 
                { value : 'S',  text : 'Seasonal'}, 
                { value : 'P',  text : 'Probationary'}, 
                { value : 'AL', text : 'Apprentices/Learners'}, 
            ],
            formattedTin: '',
            employeePreviousSalaryData: {
                employeeSalaryInfoPreviousSeq: null,
                companySeq: null,
                employeeSeq: null,
                employerName: '',
                employmentType : '',
                tinNo: '',
                address: '',
                zipCode: '',
                taxableYear: '',
                taxableFrom: '',
                taxableTo: '',
                resignationType : '',
                nonTaxableBasicSalary: null,
                nonTaxable13thMonthAndOtherBenefits: null,
                nonTaxableSssGsisPagibigAndUnionDues: null,
                nonTaxableSalariesAndOtherFormsOfCompensation: null,
                nonTaxableDeminimisBenefits: null,
                taxableBasicSalary: null,
                taxable13thMonthAndOtherBenefits: null,
                taxableSalariesOtherCompensation: null,
                taxWithheldPresentEmployer: null,
            },
            beforeEditData: null,
        };
    },
    mounted() {
        const self = this;
        self.yearOptions = self.getYearOptions();
        self.getPreviousSalary();
    },
    computed: {
        totalNonTaxableCompensation() {
            const previousSalary = this.employeePreviousSalaryData || {};
            const nonTaxableList = [
                previousSalary.nonTaxableBasicSalary,
                previousSalary.nonTaxable13thMonthAndOtherBenefits,
                previousSalary.nonTaxableSssGsisPagibigAndUnionDues,
                previousSalary.nonTaxableSalariesAndOtherFormsOfCompensation,
                previousSalary.nonTaxableDeminimisBenefits,
            ];
            return this.sumOrNull(nonTaxableList);
        },
        totalTaxableCompensation() {
            const previousSalary = this.employeePreviousSalaryData || {};
            const taxableList = [
                previousSalary.taxableBasicSalary,
                previousSalary.taxable13thMonthAndOtherBenefits,
                previousSalary.taxableSalariesOtherCompensation,
            ];
            return this.sumOrNull(taxableList);
        },
    },
    methods: {
        getPreviousSalary() {
            const self = this;

            if (self.employeeData == null || self.employeeData.employeeSeq == null) {
                return;
            }

            const url = self.$api('uri', 'get-salary-previous');
            let jsonParam = JSON.stringify(self.employeeData.employeeSeq);
            self.$axios
                .get(url, { params: { jsonParam } })
                .then((res) => {
                    if (res.data.data.employeePreviousSalaryData == null) {
                        self.employeePreviousSalaryData.companySeq = self.employeeData.companySeq;
                        self.employeePreviousSalaryData.employeeSeq = self.employeeData.employeeSeq;
                    } else {
                        self.employeePreviousSalaryData = res.data.data.employeePreviousSalaryData;
                    }
                    self.formattedTin = self.tinNumberFormat(self.employeePreviousSalaryData.tinNo);
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        sumOrNull(list) {
            if (!Array.isArray(list) || list.length === 0) {
                return null;
            }

            const allEmpty = list.every((v) => v == null || v === '');
            if (allEmpty) {
                return null;
            }

            const toNumber = (v) => {
                if (v == null || v === '') return 0;
                const n = Number(v);
                return Number.isFinite(n) ? n : 0;
            };

            // 자바스크립트 소수점 연산 오차를 줄이기 위해 100배 정수 단위로 합산 후 원래 단위로 되돌림
            const scaledTotal = list.reduce(
                (acc, curr) => acc + Math.trunc(toNumber(curr) * 100),
                0
            );
            return scaledTotal / 100;
        },
        getYearOptions() {
            const currentYear = new Date().getFullYear();
            const previousYear = currentYear - 1;

            return [currentYear, previousYear].map((y) => ({ text: y.toString(), value: y.toString() }));
        },
        toDatePickerValue(value) {
            if (!value) {
                return null;
            }
            if (value instanceof Date) {
                return value;
            }
            return moment(String(value), 'MMDD').toDate();
        },
        startEditPrevious() {
            const self = this;
            self.beforeEditData = { ...self.employeePreviousSalaryData };
            self.isEditingMode = true;
        },
        cancelEditPrevious() {
            const self = this;
            if (self.beforeEditData) {
                self.employeePreviousSalaryData = { ...self.beforeEditData };
                self.formattedTin = self.tinNumberFormat(self.employeePreviousSalaryData.tinNo);
            }
            self.isEditingMode = false;
        },
        insertOrEditPreviousSalary() {
            const self = this;

            const salaryPreviousData = { ...self.employeePreviousSalaryData };
            if (self.invalidEmployeeSalaryPreviousForm(salaryPreviousData)) {
                return;
            }

            const requestItem = { ...salaryPreviousData };
            const url = self.$api('uri', 'post-salary-previous');
            self.$axios
                .post(url, requestItem)
                .then((res) => {
                    self.employeePreviousSalaryData = res.data.data.employeePreviousSalaryData;
                    self.formattedTin = self.tinNumberFormat(self.employeePreviousSalaryData.tinNo);
                    self.beforeEditData = null;
                    self.isEditingMode = false;
                })
                .catch((err) => {
                    console.log(err.response);
                });
        },
        invalidEmployeeSalaryPreviousForm(formData) {
            const self = this;

            const { employerName, tinNo, taxableYear, taxableFrom, taxableTo, employmentType, resignationType } = formData;
            if ([employerName, tinNo, taxableYear, taxableFrom, taxableTo, employmentType, resignationType].some((i) => !i)) {
                let msg = 'Please provide the required information. These field cannot be left empty.\n';
                msg += 'Employee Name, Tin Number, Taxable Year, Taxable From Date , Taxable To Date, Employment Type, Resignation Type';
                alert(msg);
                return true;
            }

            for (let key in formData) {
                if (key == 'employeeSalaryInfoPreviousSeq' || key == 'employerName' || key == 'address' || key == 'resignationType' || key == 'employmentType') {
                    continue;
                } else if (key == 'companySeq') {
                    if (ValidateUtil.invalidString(formData.companySeq)) {
                        alert('Company information is unknown. Please reload.');
                        return true;
                    }
                } else if (key == 'employeeSeq') {
                    if (ValidateUtil.invalidString(formData.employeeSeq)) {
                        alert('Employee information is unknown. Please reload.');
                        return true;
                    }
                } else if (key == 'tinNo') {
                    if (formData.tinNo.length < 12 || formData.tinNo.length > 14) {
                        alert('Please enter a valid Tax Identification Number (TIN) in the format of 12 to 14 digits');
                        return true;
                    }
                } else if (key == 'zipCode') {
                    if (formData.zipCode.length != 4) {
                        alert('ZIP Code digits is must be 4');
                        return true;
                    }
                } else if (key == 'taxableYear') {
                    if (formData.taxableYear.length != 4) {
                        alert(`Taxable Year format is [YYYY] ${formData.taxableYear.length}`);
                        return true;
                    }
                } else if (key == 'taxableFrom') {
                    if (formData.taxableFrom.length != 4) {
                        alert('Taxable From format is [MMDD]');
                        return true;
                    }
                } else if (key == 'taxableTo') {
                    if (formData.taxableTo.length != 4) {
                        alert('Taxable to format is [MMDD]');
                        return true;
                    }
                }
                else {
                    // 나머지는 값 타입 임.
                    formData[key] = String(formData[key]).replaceAll(',', '');
                    formData[key] = Number(Number.parseFloat(formData[key]).toFixed(2));
                    if (self.invalidDecimal(formData[key])) {
                        return true;
                    }
                }
            }

            return false;
        },
        invalidDecimal(value) {
            // 현재 서버는 Decimal 18, 2 이지만
            // javascript Integer Precision 이 15 digits 이기 때문에 13, 2로 제한 합니다.
            let epsilon = 0.000001;
            let min = -0.01;
            let max = 10000000000000;
            if (Number.isNaN(value)) {
                alert('Only numbers must be entered for data other than your previous employer information.');
                return true;
            }

            if (ValidateUtil.invalidDecimal(min, max, epsilon, value)) {
                alert('Amount must be 0 or more and less than 10,000,000,000,000.(0.01 ~ 10,000,000,000,000');
                console.log(value);
                return true;
            }

            return false;
        },
        tinNumberFormat(input) {
            const self = this;

            const inputValue = input?.replace(/\D/g, ''); // Remove non-digit characters
            let formattedValue = '';

            if (inputValue) {
                //
                formattedValue = inputValue.match(new RegExp('.{1,3}', 'g')).join('-'); // xxx-xxx-xxx
                const regex = /^(\d{3})(\d{3})(\d{3})(\d{1,})$/;
                const match = regex.exec(inputValue);

                if (match) {
                    formattedValue = `${match[1]}-${match[2]}-${match[3]}-${match[4]}`; // xxx-xxx-xxxxx
                    self.formattedTin = formattedValue;
                }
            }

            return formattedValue;
        },
        changeTinNumberFormat(input) {
            const self = this;
            if (!input) {
                self.employeePreviousSalaryData.tinNo = '';
                self.formattedTin = '';
                return;
            }

            const inputValue = input?.replace(/\D/g, '');
            self.employeePreviousSalaryData.tinNo = inputValue;

            let formattedValue = '';
            if (inputValue) {
                formattedValue = inputValue.match(new RegExp('.{1,3}', 'g')).join('-');
                const regex = /^(\d{3})(\d{3})(\d{3})(\d{1,})$/;
                const match = regex.exec(inputValue);

                if (match) {
                    formattedValue = `${match[1]}-${match[2]}-${match[3]}-${match[4]}`;
                }
            }

            self.formattedTin = formattedValue;
        },
        displayResignationType(typeValue){
            switch (typeValue) {
                case 'T': return "Terminated/Resigned"
                case 'TR': return "Transferred"
                case 'R': return "Retirement"
                case 'D': return "Death"
                default: return '';
            }
        },
        displayPreviousEmployment(typeValue){
            switch (typeValue) {
                case 'R': return 'Regular';
                case 'C': return 'Casual';
                case 'CP': return 'Contractual/Project-Based';
                case 'S': return 'Seasonal';
                case 'P': return 'Probationary';
                case 'AL': return 'Apprentices/Learners';
                default : return '';
            }
            
        }
    },
};
</script>
